import { FC, MouseEvent, ReactNode } from "react";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";

import { isTest } from "@/common/constants/env";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronDown, ChevronUp } from "@/icons/outlined";

export interface CheckInAccordionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  isMobile: boolean;
  rowGap: number;
  contentGap?: number;
  chipLabel?: string;
  action?: ReactNode;
  children: ReactNode;
  "data-test"?: string;
}

export const CheckInAccordion: FC<CheckInAccordionProps> = ({
  title,
  isOpen,
  onToggle,
  isMobile,
  rowGap,
  contentGap = 2,
  chipLabel,
  action,
  children,
  "data-test": dataTest,
}) => {
  const handleChevronClick = (event: MouseEvent) => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <Box
      data-test={dataTest}
      sx={(theme) => ({
        backgroundColor: theme.palette.surface.neutral.white,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        borderRadius: theme.radius(1),
        padding: 1.5,
      })}
    >
      <Stack direction="row" alignItems="center" gap={0.5} onClick={onToggle} sx={{ cursor: "pointer" }}>
        <DashboardTypography
          variant={isMobile ? "subtitle2" : "subtitle1"}
          vibe="text-neutral-intense"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {title}
        </DashboardTypography>
        {chipLabel && <DashboardChip variant="outlined" vibe="brand" label={chipLabel} />}
        {action && <Box onClick={(event) => event.stopPropagation()}>{action}</Box>}
        <DashboardIconButton
          variant="text"
          vibe="neutral"
          size="small"
          onClick={handleChevronClick}
          aria-expanded={isOpen}
          aria-label={title}
          data-test={dataTest ? `${dataTest}-toggle` : undefined}
        >
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </DashboardIconButton>
      </Stack>
      <Collapse in={isOpen} timeout={isTest ? 0 : "auto"} unmountOnExit>
        <Stack gap={contentGap} sx={{ paddingTop: 1 }}>
          <DashboardDivider />
          <Stack gap={rowGap}>{children}</Stack>
        </Stack>
      </Collapse>
    </Box>
  );
};
